/**
 * OTP verification step of the passwordless sign-in flow. Owns the
 * resend-countdown pipeline in its own connectedCallback/disconnectedCallback
 * lifecycle, scoped to this component — not the parent orchestrator.
 *
 * Dispatches:
 *   sign-code-verify  — user submitted a 6-digit code; detail { code }
 *   sign-code-resend  — user requested a new code (after countdown expires)
 *   sign-code-back    — user tapped "Use a different email"
 *
 * Data down via properties (.email, .loading, .error, .invite). Intent up
 * via events. clearCode() resets the OTP input — called by the orchestrator
 * after a verify failure.
 *
 * Verifying state has one locus — the code field drops to 70% opacity and
 * the Verify button carries the busy treatment. The verifying
 * condition is `this.loading || this.submitting`.
 *
 * .invite — present only when sendOTP resolved a live pending invite for
 * this email; rendered as a banner above the code field so the invitee sees
 * what they're joining BEFORE typing the code.
 */
import { otp } from "@mhmo91/schmancy/directives";
import type { SchmancyInput } from "@mhmo91/schmancy/form";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html, nothing, type PropertyValues } from "lit";
import { property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { when } from "lit/directives/when.js";
import { interval, Subject } from "rxjs";
import { take, takeUntil, tap } from "rxjs/operators";
import { LocalizeMixin } from "../i18n/localize.ts";
import type { InviteContext } from "./sign.state.ts";

/** The resend timer waits what the server's send cooldown refuses inside
 *  (sendOTP.api.ts SEND_COOLDOWN_MS), so the button never offers a refusal. */
const RESEND_COOLDOWN_SECONDS = 30;

export class SignCodeVerifyEvent extends CustomEvent<{ code: string }> {
	static readonly eventName = "sign-code-verify" as const;
	constructor(code: string) {
		super(SignCodeVerifyEvent.eventName, {
			detail: { code },
			bubbles: true,
			composed: true,
		});
	}
}

export class SignCodeResendEvent extends CustomEvent<void> {
	static readonly eventName = "sign-code-resend" as const;
	constructor() {
		super(SignCodeResendEvent.eventName, { bubbles: true, composed: true });
	}
}

export class SignCodeBackEvent extends CustomEvent<void> {
	static readonly eventName = "sign-code-back" as const;
	constructor() {
		super(SignCodeBackEvent.eventName, { bubbles: true, composed: true });
	}
}

@schmancyElement("sign-code")
export class SignCodeStep extends LocalizeMixin(SchmancyElement) {
	@property({ type: String }) email = "";
	@property({ type: String }) heading = "";
	@property({ type: String }) instruction = "";
	@property({ type: String }) actionLabel = "";
	@property({ type: Boolean }) allowBack = true;
	@property({ type: Number }) resendAt = 0;
	@property({ type: Boolean }) loading = false;
	@property({ attribute: false }) pendingAction: "verify" | "resend" | null =
		null;
	@property({ type: String }) error = "";
	/** sendOTP's own invite-context projection — null on an ordinary sign-in. */
	@property({ attribute: false }) invite: InviteContext | null = null;

	@state() private code = "";
	@state() private resendCooldown = RESEND_COOLDOWN_SECONDS;
	@state() private submitting = false;

	/**
	 * The code already handed to verify. Re-entering the identical string is
	 * the user asserting the same answer twice, which the parent's own
	 * clearCode() re-arms; any other edit of a full field is a new answer.
	 */
	private submittedCode = "";

	private otpRef = createRef<SchmancyInput>();
	private cooldownStop$ = new Subject<void>();

	override connectedCallback() {
		super.connectedCallback();
		this.startCooldown();
	}

	override disconnectedCallback() {
		super.disconnectedCallback();
		this.cooldownStop$.next();
	}

	protected override updated(changed: PropertyValues): void {
		super.updated(changed);
		if (changed.has("resendAt") && this.resendAt > 0) this.startCooldown();
	}
	startCooldown() {
		this.resendCooldown =
			this.resendAt > 0
				? Math.max(0, Math.ceil((this.resendAt - Date.now()) / 1000))
				: RESEND_COOLDOWN_SECONDS;
		this.cooldownStop$.next();
		interval(1000)
			.pipe(
				take(this.resendCooldown),
				tap(() => {
					this.resendCooldown--;
				}),
				takeUntil(this.cooldownStop$),
				takeUntil(this.disconnecting),
			)
			.subscribe();
	}

	clearCode() {
		this.code = "";
		this.submitting = false;
		this.submittedCode = "";
		if (this.otpRef.value) this.otpRef.value.value = "";
		void this.updateComplete
			.then(() => this.otpRef.value?.updateComplete)
			.then(() => this.otpRef.value?.focus());
	}

	private verify() {
		if (this.code.length !== 6 || this.loading || this.submitting) return;
		this.submittedCode = this.code;
		this.submitting = true;
		this.dispatchEvent(new SignCodeVerifyEvent(this.code));
	}

	override render() {
		const invite = this.invite;
		const inviteBanner = invite
			? this.t("auth.inviteContext.invitedTo", {
					workspaceName: invite.workspaceName,
					roleName: invite.roleName,
					invitedBy: invite.invitedBy,
				})
			: null;

		return html`<div class="flex flex-col gap-4 p-5">
   <div class="flex items-start justify-between gap-3"><div class="min-w-0 flex flex-col gap-1"><schmancy-typography type="headline" token="md">${this.heading || this.t("auth.signCode.verify")}</schmancy-typography>${this.instruction ? html`<schmancy-typography type="body" token="md">${this.instruction}</schmancy-typography>` : nothing}</div><slot name="header-actions"></slot></div>
   ${when(inviteBanner !== null, () => html`<schmancy-surface type="info" rounded="all" class="px-4 py-3 rounded-xl"><schmancy-typography type="body" token="sm">${inviteBanner}</schmancy-typography></schmancy-surface>`)}
   <div class="flex flex-col gap-3"><schmancy-typography type="body" token="sm" class="break-all">${this.email}</schmancy-typography><div class=${this.classMap({ "transition-opacity duration-300": true, "opacity-100": !(this.loading || this.submitting), "opacity-70": this.loading || this.submitting })}>
    <schmancy-input ${ref(this.otpRef)} ${otp({ length: 6, alignment: "center" })} otp autofocus maxlength="6" size="md" class="block w-full" aria-label=${this.t("auth.otpInput.groupLabel")} .value=${this.code} ?disabled=${this.loading || this.submitting} ?error=${!!this.error && !this.loading && !this.submitting} @input=${(
			event: CustomEvent<{ value: string }>,
		) => {
			this.code = event.detail.value;
			if (this.code.length === 6 && this.code !== this.submittedCode)
				this.verify();
		}}></schmancy-input>
   </div></div>
   ${this.error && !this.loading && !this.submitting ? html`<schmancy-surface type="error" rounded="all" class="rounded-xl px-4 py-3" aria-live="polite"><schmancy-typography type="body" token="sm" role="alert">${this.error}</schmancy-typography></schmancy-surface>` : nothing}
   <div class="flex flex-wrap items-center justify-between gap-2"><schmancy-button variant="text" size="md" ?disabled=${this.resendCooldown > 0 || (this.loading && this.pendingAction !== "resend")} .busy=${this.loading && this.pendingAction === "resend"} @click=${() => {
			if (this.resendCooldown > 0 || this.loading || this.submitting) return;
			this.dispatchEvent(new SignCodeResendEvent());
		}}>${this.resendCooldown > 0 ? this.t("auth.signCode.resendIn", { seconds: this.resendCooldown }) : this.t("auth.signCode.resendReady")}</schmancy-button><schmancy-button variant="filled" class="ml-auto" size="md" ?disabled=${this.code.length !== 6 || (this.loading && this.pendingAction === "resend")} .busy=${this.pendingAction !== "resend" && (this.loading || this.submitting)} @click=${() => this.verify()}>${this.actionLabel || this.t("auth.signCode.verify")}</schmancy-button></div>
   ${this.allowBack ? html`<schmancy-button variant="text" class="self-start" size="md" ?disabled=${this.loading} @click=${() => this.dispatchEvent(new SignCodeBackEvent())}><schmancy-icon slot="prefix">arrow_back</schmancy-icon>${this.t("auth.signCode.useDifferentEmail")}</schmancy-button>` : nothing}
  </div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"sign-code": SignCodeStep;
	}
}
